import React, { useState, useMemo, useEffect } from "react";
// import { GladlySwitcher } from "@/modules/gladly";
import { ChatPage } from "@/pages/chat";
import { FAQPage } from "@/pages/faq";
import { FAQDetailPage } from "@/pages/faq";
import { Toaster } from "@/components/ui/toaster";
import { useToast } from "@/hooks/useToast";
import { useChatStore } from "@/stores/chatStore";
import { getAccessToken } from "@/utils/auth";
import { cn } from "@/lib/utils";
import { fetchChatHistory } from "@/service";
import { apiGet, apiPost } from "@/utils/apiHelper";
import type { APIResponse } from "@/utils/apiHelper";
import { LOGIN_API, CHAT_API } from "@/config/api";
import { throwCasaApiError } from "@/utils/errorHandler";
import { CasaEntryButton } from "./CasaEntryButton";
import {
  openGladly,
  closeGladly,
  shouldCheckChatMode,
  shouldPromptGladlyRefresh,
  waitForGladlyReady,
  GLADLY_REFRESH_MESSAGE,
  type ChatModeData,
} from "../utils/chatMode";

const SERVICE_CLIENT_GAP = 1000;
const PULL_UNREAD_MESSAGE_INTERVAL = 15000;

export const CasaSwitcher: React.FC = () => {
  const {
    abVariant,
    entryPath,
    setEntryPath,
    currentView,
    chatExpanded,
    hadHandledProactiveGreeting,
    isLoginInProgress,
    setLoginInProgress,
    openChat,
    closeChat,
    setCurrentView,
    lastMessageTimestamp,
    hadUnreadMessage,
    addHadUnReadMessage,
    reset,
  } = useChatStore();
  const { toast } = useToast();

  // 添加视口高度检测
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [isViewportSmall, setIsViewportSmall] = useState(false);

  // 视口高度阈值，小于此值时启用顶部对齐
  const VIEWPORT_HEIGHT_THRESHOLD = 667; // 可以根据需要调整

  useEffect(() => {
    const handleResize = () => {
      const height = window.innerHeight;
      setViewportHeight(height);
      setIsViewportSmall(height < VIEWPORT_HEIGHT_THRESHOLD);
    };

    // 初始检测
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const needPolling = useMemo(() => {
    return (
      (chatExpanded === false || currentView === "faq") && !hadUnreadMessage
    );
  }, [chatExpanded, currentView, hadUnreadMessage]);

  // 监听 Cookie 中 access_token 的变化
  useEffect(() => {
    let previousToken = getAccessToken();

    // 处理 access_token 变化的函数
    const handleAccessTokenChange = (newToken: string | null) => {
      const hadToken = previousToken !== null;
      const hasToken = newToken !== null;

      console.log("Access token changed:", {
        from: previousToken ? "[has token]" : "[no token]",
        to: newToken ? "[has token]" : "[no token]",
        hadToken,
        hasToken,
      });

      // 从无到有：用户登录
      if (!hadToken && hasToken) {
        const { chatExpanded, currentView } = useChatStore.getState();
        reset();
        requestAnimationFrame(() => {
          setCurrentView(currentView);
          if (chatExpanded) {
            openChat();
          }
        });

        console.log("User logged in - token acquired");
        apiPost(LOGIN_API.loginCallback());
      }

      // 从有到无：用户登出
      else if (hadToken && !hasToken) {
        reset();
        console.log("User logged out - token removed");
        apiPost(LOGIN_API.logoutCallback(), null, {
          "X-Access-Token": previousToken,
        });
      }

      previousToken = newToken;
    };

    // CookieStore 事件处理函数 - 需要抽出来以便移除监听器
    const handleCookieStoreChange = (event: any) => {
      const accessTokenChange =
        event.changed.find((cookie: any) => cookie.name === "access_token") ||
        event.deleted.find((cookie: any) => cookie.name === "access_token");

      if (accessTokenChange) {
        const newToken = getAccessToken();
        handleAccessTokenChange(newToken);
      }
    };

    // 使用 cookieStore API (如果支持)
    let cookieStoreSupported = false;
    if ("cookieStore" in window) {
      try {
        // 监听 access_token cookie 的变化
        window.cookieStore.addEventListener("change", handleCookieStoreChange);
        cookieStoreSupported = true;
        console.log("CookieStore API is supported and listening");
      } catch (error) {
        console.log("CookieStore API failed, falling back to polling:", error);
      }
    }

    // 降级方案：使用轮询检测 cookie 变化
    let pollInterval: ReturnType<typeof setInterval>;
    if (!cookieStoreSupported) {
      console.log("Using polling fallback for cookie changes");
      pollInterval = setInterval(() => {
        const currentToken = getAccessToken();
        if (currentToken !== previousToken) {
          handleAccessTokenChange(currentToken);
        }
      }, 1000); // 每秒检查一次
    }

    return () => {
      // 正确移除 cookieStore 监听器
      if (cookieStoreSupported && "cookieStore" in window) {
        try {
          window.cookieStore.removeEventListener(
            "change",
            handleCookieStoreChange,
          );
          console.log("CookieStore listener removed");
        } catch (error) {
          console.log("Error removing cookieStore listener:", error);
        }
      }
      // 清理轮询定时器
      if (pollInterval) {
        clearInterval(pollInterval);
        console.log("Cookie polling interval cleared");
      }
    };
  }, []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let isMounted = true;

    const pollHistory = async () => {
      if (isMounted && needPolling) {
        try {
          const { list } = await fetchChatHistory({
            page: 0,
            pageSize: 1,
          });
          const lastMessage = list[list.length - 1];
          if (lastMessage) {
            const newLastMessageTimestamp = new Date(
              lastMessage.created_at,
            ).getTime();
            if (lastMessageTimestamp !== null) {
              // TODO：目前存在误差，后续需要后端提供接口返回最后一条AI消息
              if (
                newLastMessageTimestamp - lastMessageTimestamp >
                SERVICE_CLIENT_GAP
              ) {
                addHadUnReadMessage();
              }
            }
          }
        } catch (error) {
          /**
           * TODO: skip error handling for polling to avoid noisy logs
           */
          // console.error("Error polling chat history:", error);
          // throwCasaApiError(error);
        } finally {
          if (isMounted && needPolling) {
            timeoutId = setTimeout(pollHistory, PULL_UNREAD_MESSAGE_INTERVAL);
          }
        }
      }
    };

    pollHistory();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [chatExpanded, needPolling, lastMessageTimestamp]);

  // 判断是否应该触发 Proactive Greeting
  const shouldTriggerProactiveGreeting = (): boolean => {
    const currentPath = window.location.pathname;
    // https://www.castlery.co/sg/products/xxx
    const isProductPage = currentPath.includes("/products/");

    return isProductPage && !hadHandledProactiveGreeting;
  };

  useEffect(() => {
    const PROACTIVE_GREETING_INTERVAL = 60 * 1000;
    let timer: ReturnType<typeof setTimeout>;

    // 启动计时器的函数
    const startTimer = () => {
      const shouldTrigger = shouldTriggerProactiveGreeting();
      if (shouldTrigger) {
        timer = setTimeout(() => {
          setEntryPath("proactive");
        }, PROACTIVE_GREETING_INTERVAL);
      }
    };

    // 清除计时器的函数
    const clearTimer = () => {
      if (timer) {
        clearTimeout(timer);
      }
    };

    // 处理页面可见性变化
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // 页面隐藏时清除计时器
        clearTimer();
      } else {
        // 页面重新可见时重新加载持久化状态
        useChatStore.persist.rehydrate();

        // 页面可见时重新启动计时器
        clearTimer(); // 先清除旧的计时器
        startTimer();
      }
    };

    // 初始启动计时器
    startTimer();

    // 监听页面可见性变化
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearTimer();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!shouldCheckChatMode()) {
      return;
    }

    let currentCheckId = 0;

    const checkChatMode = async () => {
      const checkId = ++currentCheckId;
      try {
        const response = await apiGet<APIResponse<ChatModeData>>(
          CHAT_API.mode(),
        );
        if (response.code !== 0) {
          throw new Error("Failed to check chat mode");
        }
        if (checkId !== currentCheckId) return;

        if (response.data.mode === "gladly") {
          await waitForGladlyReady();
          if (checkId !== currentCheckId) return;
          if (useChatStore.getState().chatExpanded) {
            closeChat();
          }
          await openGladly();
        } else if (response.data.mode === "casa") {
          await waitForGladlyReady();
          if (checkId !== currentCheckId) return;
          await closeGladly();
        }
      } catch (error) {
        throwCasaApiError(error, "[Casa api error]: Failed to check chat mode");
        if (shouldPromptGladlyRefresh(error)) {
          toast({
            description: GLADLY_REFRESH_MESSAGE,
          });
        }
      }
    };

    checkChatMode();

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkChatMode();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      currentCheckId++;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // TODO: 检测登录完成后自动打开 ChatPage
  useEffect(() => {
    const checkLoginCompletion = () => {
      if (isLoginInProgress) {
        const accessToken = getAccessToken();

        // 登录成功打开 ChatPage
        if (accessToken !== null) {
          console.log("Login completed, opening ChatPage...");

          // 清除登录流程标记
          setLoginInProgress(false);

          openChat();
        }
      }
    };

    // 组件挂载时检查一次
    checkLoginCompletion();

    // 监听 storage 事件（跨标签页通信）
    const handleStorageChange = (e: StorageEvent) => {
      // FIXME: access_token 需要从 cookie 中获取
      if (isLoginInProgress || e.key === "access_token") {
        checkLoginCompletion();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [isLoginInProgress]);

  // 检测用户登录状态
  // const detectUserLoginStatus = (): UserLoginStatus => {
  //   const hasAuthToken =
  //     localStorage.getItem("auth_token") ||
  //     document.cookie.includes("session_id") ||
  //     localStorage.getItem("user_id");

  //   if (hasAuthToken) {
  //     return "logged_in";
  //   }

  //   return "logged_out";
  // };

  // if (abVariant === "gladly") {
  //   return <GladlySwitcher />;
  // }

  if (!chatExpanded) {
    const isProactiveGreetingEnabled =
      abVariant === "ai" &&
      entryPath === "proactive" &&
      !hadHandledProactiveGreeting;

    // 聊天按钮
    return <CasaEntryButton enable={isProactiveGreetingEnabled} />;
  }

  return (
    <>
      <div
        id="psa-chat-framework-wrapper"
        className={cn({
          // 固定属性
          "fixed overflow-hidden !z-[2147483000]": true,
          // 移动端：占满整个屏幕
          "top-0 left-0 right-0 bottom-0": true,
          // 桌面端：根据视口高度动态调整位置
          "sm:left-auto sm:w-[390px] sm:rounded-lg sm:border sm:border-primary sm:shadow-lg": true,
          // 视口高度较小时：顶部对齐
          "sm:top-0 sm:right-0 sm:bottom-0 sm:h-auto sm:max-h-[100vh]":
            isViewportSmall,
          // 视口高度正常时：底部对齐，固定高度
          "sm:top-auto sm:right-[15px] sm:bottom-[15px] sm:h-[652px]":
            !isViewportSmall,
        })}
      >
        {currentView === "faq" && <FAQPage />}
        {currentView === "faq-detail" && <FAQDetailPage />}
        {currentView === "chatbox" && <ChatPage />}

        {/* Toast 通知 */}
        <Toaster />
      </div>
    </>
  );
};
